import React, { useState, useEffect, useRef } from "react";
import Sidebar from "../../../../components/sidebar";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../style.scss";
import ClockYellowIcon from "../../../../icons/ClockYellowIcon";
import SearchIcon from "../../../../icons/SearchIcon";
import LocationIcon from "../../../../icons/LocationIcon";
import TimerGreyIcon from "../../../../icons/TimerGreyIcon";
import UploadIcon from "../../../../icons/UploadIcon";
import FileUploadWhiteIcon from "../../../../icons/FileUploadWhiteIcon";
import TravelActivityIcon from "../../../../icons/TravelActivityIcon";
import ArrowUpWhiteIcon from "../../../../icons/ArrowUpWhiteIcon";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import LogHourEntryModal from "../../../../core/components/modals/logHourEntryModal";
import { LogHourEntry } from "../../../../constants/types";
import { toast } from "react-toastify";
import {
  activityList,
  mainCategoryList,
  subActivityList,
} from "../../../../services/category.service";
import { getPropertyListForFilter } from "../../../../core/data/redux/property/_propertyAction";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  addMileage,
  getMileageDetails,
  updateMileage,
} from "../../../../core/data/redux/mileage/_mileageAction";
import DeleteModal from "../../../../components/deleteModal";
import { recurringDays } from "../../../../constants/utlis";
import { Autocomplete, DirectionsRenderer, GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MAX_FILE_SIZE, MAX_FILES, MAX_TOTAL_SIZE } from "../../../../constants/common";
import { formatDateStringType } from "../../../../constants/date";

const libraries: ("places")[] = ["places"];
const defaultCenter = { lat: 37.0902, lng: -95.7129 }; // United States center

const initialValues = {
  property: "",
  source_name: "",
  destination_name: "",
  source_coordinates: "",
  destination_coordinates: "",
  type: "stop",
  route_trip: false,
  distance: "",
  purpose: "",
  main_category: "",
  sub_activity: "",
  activity: "",
  activity_description: "",
  date: "",
  is_recurring: false,
  frequencies: "",
  receipt_images: null,
  is_travel: false,
  logHourData: null,
};

const validationSchema = Yup.object({
  property: Yup.string().required("Please select a property"),
  source_name: Yup.string().required("Starting location is required"),
  destination_name: Yup.string().required("Destination location is required"),
  source_coordinates: Yup.string().required(
    "Starting coordinates are required"
  ),
  destination_coordinates: Yup.string().required(
    "Destination coordinates are required"
  ),
  type: Yup.string().required("Type is required"),
  route_trip: Yup.boolean(),
  distance: Yup.string().required("Distance is required"),
  purpose: Yup.string().max(500, "Maximum 500 characters"),
  main_category: Yup.string().required("Please choose activity category"),

  sub_activity: Yup.string().when("main_category", {
    is: (val: string) => !!val,
    then: (schema) => schema.required("Please choose sub activity"),
    otherwise: (schema) => schema.notRequired(),
  }),

  activity: Yup.string().when(["main_category", "sub_activity"], {
    is: (cat: string, sub: string) => !!cat && !!sub,
    then: (schema) => schema.required("Please choose activity"),
    otherwise: (schema) => schema.notRequired(),
  }),

  activity_description: Yup.string()
    .max(500, "Maximum 500 characters")
    .nullable(),
  date: Yup.string().required("Please pick a date"),
  is_recurring: Yup.boolean(),
  frequencies: Yup.string().when("is_recurring", {
    is: true,
    then: (schema) => schema.required("Days are required for recurring trips"),
    otherwise: (schema) => schema.notRequired(),
  }),
  receipt_images: Yup.mixed().nullable(),

  is_travel: Yup.boolean(),
  logHourData: Yup.object()
    .nullable()
    .when("is_travel", {
      is: true,
      then: (schema) =>
        schema
          .shape({
            hours: Yup.number()
              .typeError("Hours must be a number")
              .min(0, "Hours cannot be less than 0")
              .max(24, "Hours cannot be more than 24")
              .test(
                "hours-or-minutes",
                "Either hours or minutes must be greater than 0",
                function (value) {
                  const { minutes } = this.parent;
                  return (value ?? 0) > 0 || (minutes ?? 0) > 0;
                }
              ),
            minutes: Yup.number()
              .typeError("Minutes must be a number")
              .min(0, "Minutes cannot be less than 0")
              .max(60, "Minutes cannot be more than 60")
              .test(
                "hours-or-minutes",
                "Either hours or minutes must be greater than 0",
                function (value) {
                  const { hours } = this.parent;
                  return (value ?? 0) > 0 || (hours ?? 0) > 0;
                }
              ),
            notes: Yup.string().max(500, "Maximum 500 characters"),
          })
          .required("Log Hour entry is required when travel is involved")
          .typeError("Log Hour entry must be an object"),
      otherwise: (schema) => schema.notRequired(),
    }),
});

const AddMileage: React.FC = () => {
  const { isSidebarOpen } = useOutletContext<{ isSidebarOpen: boolean; onToggleSidebar: () => void }>();
  const { id } = useParams<{ id: string }>(); // id in route => edit mode
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const routes = all_routes;
  const isEdit = Boolean(id);

  // Proper Google Maps loader
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
    libraries,
  });

  const [showLogHourModal, setShowLogHourModal] = useState(false);
  const [logHourData, setLogHourData] = useState<LogHourEntry | null>(null);
  const [mainCategories, setMainCategories] = useState<{ label: string; value: string }[]>([]);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [receiptImages, setReceiptImages] = useState<any[]>([]);
  const [subActivities, setSubActivities] = useState<{ label: string; value: string }[]>([]);
  const [activities, setActivities] = useState<{ label: string; value: string }[]>([]);
  const [selectedImageUuid, setSelectedImageUuid] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [receiptFiles, setReceiptFiles] = useState<File[]>([]);
  const [uploadError, setUploadError] = useState("");
  const [isTravelInvolved, setIsTravelInvolved] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [showActivityDescription, setShowActivityDescription] = useState(false);
  const [startAutocomplete, setStartAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [endAutocomplete, setEndAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [startAddress, setStartAddress] = useState("");
  const [endAddress, setEndAddress] = useState("");
  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  const [distance, setDistance] = useState<string>("");
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapLoading, setMapLoading] = useState(true);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isLoaded) {
      setMapLoading(false);
    }
  }, [isLoaded]);

  // ADD THIS ENTIRE BLOCK BELOW (Fixed version with no typo)
  useEffect(() => {
    if (directionsResponse && map && isLoaded) {
      const bounds = new window.google.maps.LatLngBounds();

      directionsResponse.routes.forEach((route) => {
        route.legs.forEach((leg) => {
          if (leg.start_location) bounds.extend(leg.start_location);
          if (leg.end_location) bounds.extend(leg.end_location);

          leg.steps.forEach((step) => {
            step.path.forEach((point) => {
              bounds.extend(point);
            });
          });
        });
      });

      map.fitBounds(bounds);

      // Prevent zooming in too much on very short trips
      setTimeout(() => {
        if (map.getZoom() && map.getZoom()! > 16) {
          map.setZoom(16);
        }
      }, 150);
    }
  }, [directionsResponse, map, isLoaded]);

  const filteredProperties = allProperties.filter((p) =>
    `${p.name} ${p.address}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    getMainCategoryList();
    getAllPropertyList();
  }, [dispatch]);

  // get main category list
  const getMainCategoryList = async () => {
    try {
      const response = await mainCategoryList();
      const data = response?.data?.data || [];

      const mapped = data.map((cat: any) => ({
        label: cat.name,
        value: cat.uuid,
      }));

      setMainCategories(mapped);
      return;
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  // getPropertyListForFilter
  const getAllPropertyList = async () => {
    try {
      const response = await dispatch(getPropertyListForFilter({}));
      const allProps = response?.payload?.data || [];
      setAllProperties(allProps);
      return;
    } catch (error) {
      console.error("Failed to fetch all property:", error);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //  Formik Setup
  const formik = useFormik({
    initialValues,
    validationSchema,
    // validateOnChange: true,  // ensure object-level tests run on change
    validateOnBlur: true,

    onSubmit: async (values) => {
      console.log("values ------- ", values);
      try {
        const formData = new FormData();

        Object.entries(values).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (typeof value === "boolean" || typeof value === "number") {
              formData.append(key, String(value));
            } else if (
              key === "logHourData" &&
              value &&
              typeof value === "object"
            ) {
              const logHour = value as LogHourEntry;
              formData.append(
                "hours",
                logHour.hours ? String(logHour.hours) : "0"
              );
              formData.append(
                "minutes",
                logHour.minutes ? String(logHour.minutes) : "0"
              );
              formData.append("notes", logHour.notes || "");
              if (isEdit && logHour.uuid) {
                formData.append("log_hour_uuid", logHour.uuid);
              }
            } else if (key === "receipt_images" && Array.isArray(value)) {
              value.forEach((file: File) =>
                formData.append("receipt_images", file)
              );
            } else if (key === "frequencies") {
              // Handle frequencies field
              const daysToSend =
                isRecurring && selectedDays.length === recurringDays.length
                  ? "everyday"
                  : selectedDays.join(", ");
              formData.append(key, daysToSend);
            } else {
              formData.append(key, value as string);
            }
          }
        });

        if (receiptFiles?.length > 0) {
          receiptFiles.forEach((file) => {
            formData.append("receipt_images", file);
          });
        }

        if (isEdit) {
          await dispatch(updateMileage({ uuid: id, values: formData }));
        } else {
          await dispatch(addMileage(formData));
        }
        navigate(routes.mileage);
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong!");
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue("is_travel", isTravelInvolved);
    formik.setFieldValue("is_recurring", isRecurring);
    formik.setFieldValue("logHourData", logHourData);
  }, [isTravelInvolved, isRecurring, logHourData]);

  // Load log hour details if editing
  useEffect(() => {
    console.log(
      "Initial Formik State:",
      formik.values,
      formik.errors,
      formik.touched
    );
    if (isEdit) {
      const fetchData = async () => {
        try {
          const response = await dispatch(getMileageDetails({ uuid: id }));
          const mileageData = response?.payload?.data;

          if (mileageData) {
            const formatCoords = (coords: string[] | null | undefined): string => {
              if (Array.isArray(coords) && coords.length === 2) {
                return `${parseFloat(coords[0]).toFixed(6)}, ${parseFloat(coords[1]).toFixed(6)}`;
              }
              return "";
            };

            const sourceCoords = formatCoords(mileageData.source_coordinates);
            const destCoords = formatCoords(mileageData.destination_coordinates);

            const formattedDate = mileageData?.date
              ? formatDateStringType(mileageData?.date)
              : "";

            const data: any = {
              property: mileageData?.property?.uuid || "",
              source_name: mileageData?.source_name || "",
              destination_name: mileageData?.destination_name || "",
              source_coordinates: sourceCoords,
              destination_coordinates: destCoords,
              // source_coordinates: mileageData?.source_coordinates?.toString() || "32543254325, 788798587",
              // destination_coordinates: mileageData?.destination_coordinates?.toString() || "32543254325, 788798587",
              type: mileageData?.type || "stop",
              route_trip: mileageData?.route_trip || false,
              distance: mileageData?.distance || "",
              purpose: mileageData?.purpose || "",
              main_category: mileageData?.main_category?.uuid || "",
              sub_activity: mileageData?.sub_activity?.uuid || "",
              activity: mileageData?.activity?.uuid || "",
              activity_description: mileageData?.activity_description || "",
              date: formattedDate,
              is_recurring: mileageData?.is_recurring || false,
              frequencies: mileageData?.frequencies?.join(", ") || "",
              receipt_images: null,
              is_travel: mileageData?.is_travel || false,
              logHourData: mileageData?.log_hour
                ? {
                  uuid: mileageData?.log_hour?.uuid || "",
                  hours: parseInt(mileageData?.log_hour?.hours || "0", 10),
                  minutes: parseInt(mileageData?.log_hour.minutes || "0", 10),
                  notes: mileageData?.log_hour?.notes || "",
                }
                : null,
            };

            // Set Formik values
            formik.setValues(data);

            // Update local state
            setStartAddress(mileageData.source_name || "");
            setEndAddress(mileageData.destination_name || "");
            setDistance(mileageData.distance ? `${parseFloat(mileageData.distance).toFixed(2)} miles` : "");
            setLogHourData(data.logHourData);
            setReceiptImages(mileageData?.receipt_images || []);
            setIsRecurring(!!mileageData?.is_recurring);
            setIsTravelInvolved(!!mileageData?.is_travel);

            if (mileageData?.is_recurring && mileageData?.frequencies) {
              if (mileageData.frequencies.includes("everyday")) {
                setSelectedDays(recurringDays);
              } else {
                setSelectedDays(
                  mileageData.frequencies.map((day: string) =>
                    day.toLowerCase()
                  )
                );
              }
            } else {
              setSelectedDays([]);
            }

            // --- Critical: Trigger map route rendering after coordinates are set ---
            if (sourceCoords && destCoords && isLoaded) {
              setTimeout(() => {
                const directionsService = new window.google.maps.DirectionsService();
                directionsService.route(
                  {
                    origin: new window.google.maps.LatLng(
                      parseFloat(sourceCoords.split(",")[0]),
                      parseFloat(sourceCoords.split(",")[1])
                    ),
                    destination: new window.google.maps.LatLng(
                      parseFloat(destCoords.split(",")[0]),
                      parseFloat(destCoords.split(",")[1])
                    ),
                    travelMode: window.google.maps.TravelMode.DRIVING,
                  },
                  (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK && result) {
                      setDirectionsResponse(result);
                    }
                  }
                );
              }, 500); // Small delay to ensure Google Maps is fully loaded
            }

          } else {
            toast.error("Failed to load mileage details");
          }
        } catch (error) {
          toast.error("Failed to load mileage details");
        }
      };
      fetchData();
    }
  }, [id, isEdit, dispatch, isLoaded]);

  //  Fetch sub activities when category changes
  useEffect(() => {
    if (formik.values.main_category) {
      subActivityList(formik.values.main_category)
        .then((res) => {
          const mapped =
            res.data?.data?.map((sub: any) => ({
              label: sub.name,
              value: sub.uuid,
            })) || [];
          setSubActivities(mapped);
          if (!isEdit) {
            formik.setFieldValue("sub_activity", "");
            setActivities([]);
          }
        })
        .catch((err) => console.error("Failed sub activities:", err));
    }
  }, [formik.values.main_category, isEdit]);

  //  Fetch activities when sub activity changes
  useEffect(() => {
    if (formik.values.main_category && formik.values.sub_activity) {
      activityList(formik.values.main_category, formik.values.sub_activity)
        .then((res) => {
          const mapped =
            res.data?.data?.map((act: any) => ({
              label: act.name,
              value: act.uuid,
            })) || [];
          setActivities(mapped);
          if (!isEdit) {
            formik.setFieldValue("activity", "");
          }
        })
        .catch((err) => console.error("Failed activities:", err));
    }
  }, [formik.values.sub_activity, isEdit]);

  useEffect(() => {
    const selectedActivity = activities.find(
      (act) => act.value === formik.values.activity
    );
    setShowActivityDescription(
      selectedActivity
        ? selectedActivity?.label?.toLowerCase() === "others"
        : false
    );
  }, [formik.values.activity, activities]);

  // Handle delete confirmation
  const handleDeleteImage = () => {
    if (selectedImageUuid) {
      setReceiptImages((prev) =>
        prev.filter((image) => image.uuid !== selectedImageUuid)
      );
      // setShowDeleteModal(false);
      setSelectedImageUuid(null);
      if (receiptFiles?.length + receiptImages?.length - 1 <= MAX_FILES) {
        setUploadError("");
      }
    }
  };

  // Handle opening delete modal
  const handleOpenDeleteModal = (uuid: string) => {
    setSelectedImageUuid(uuid);
    setShowDeleteModal(true);
  };

  // Handle closing delete modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedImageUuid(null);
  };

  // Handle successful deletion
  const handleDeleteSuccess = () => {
    handleDeleteImage();
    if (isEdit && id) {
      fetchMileageDetails(dispatch, id, setReceiptImages); // Fetch updated details
    }
    setShowDeleteModal(false);
  };

  // Add this function to fetch mileage details
  const fetchMileageDetails = async (
    dispatch: any,
    id: string,
    setReceiptImages: (images: any[]) => void
  ) => {
    try {
      const response = await dispatch(getMileageDetails({ uuid: id }));
      const mileageData = response?.payload?.data;
      if (mileageData) {
        setReceiptImages(mileageData?.receipt_images || []);
      } else {
        console.log("Failed to load mileage details");
      }
    } catch (error) {
      console.log("Failed to load mileage details");
    }
  };

  //  Handle everyday toggle
  const handleEverydayToggle = (checked: boolean) => {
    if (checked) {
      setSelectedDays([...recurringDays]);
      formik.setFieldValue("frequencies", "everyday");
    } else {
      setSelectedDays([]);
      formik.setFieldValue("frequencies", "");
    }
  };

  // Handle individual day toggle
  const handleDayToggle = (day: string) => {
    if (selectedDays.length === recurringDays.length) {
      setSelectedDays([day.toLowerCase()]);
      formik.setFieldValue("frequencies", day.toLowerCase());
      return;
    }

    if (selectedDays.includes(day.toLowerCase())) {
      const updatedDays = selectedDays.filter((d) => d !== day.toLowerCase());
      setSelectedDays(updatedDays);
      formik.setFieldValue(
        "frequencies",
        updatedDays.length > 0 ? updatedDays.join(", ") : ""
      );
    } else {
      const updatedDays = [...selectedDays, day.toLowerCase()];
      setSelectedDays(updatedDays);
      formik.setFieldValue("frequencies", updatedDays.join(", "));
    }
  };

  const handleLogHourSave = (entry: LogHourEntry | null) => {
    if (entry) {
      if (!(Number(entry?.hours) > 0 || Number(entry?.minutes) > 0)) {
        toast.error("Either hours or minutes must be greater than 0");
        return;
      }
      setLogHourData(entry);
      formik.setFieldValue("logHourData", entry, true);
      formik.setFieldTouched("logHourData", true);
    } else {
      setLogHourData(null);
      formik.setFieldValue("logHourData", null);
    }
    setShowLogHourModal(false);
  };

  //  Handle both start & destination
  const calculateRoute = async () => {
    if (
      !isLoaded ||
      !window.google ||
      !formik.values.source_coordinates ||
      !formik.values.destination_coordinates
    ) {
      setDirectionsResponse(null);
      setDistance("");
      formik.setFieldValue("distance", "");
      return;
    }

    const [lat1, lng1] = formik.values.source_coordinates.split(",").map(parseFloat);
    const [lat2, lng2] = formik.values.destination_coordinates.split(",").map(parseFloat);

    if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) {
      console.warn("Invalid coordinates:", formik.values.source_coordinates, formik.values.destination_coordinates);
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: new window.google.maps.LatLng(lat1, lng1),
        destination: new window.google.maps.LatLng(lat2, lng2),
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && result) {
          setDirectionsResponse(result);

          const leg = result.routes[0].legs[0];
          const distanceText = leg.distance?.text || "0 mi";
          const distanceValue = parseFloat(distanceText.replace(/[^\d.]/g, "")) || 0;
          const isKm = distanceText.toLowerCase().includes("km");
          const miles = isKm ? distanceValue * 0.621371 : distanceValue;
          const finalMiles = formik.values.route_trip ? miles * 2 : miles;

          setDistance(`${finalMiles.toFixed(2)} miles`);
          formik.setFieldValue("distance", finalMiles.toFixed(2));
        } else {
          console.error("Directions failed:", status);
          setDirectionsResponse(null);
          setDistance("");
          formik.setFieldValue("distance", "");
          if (status !== "ZERO_RESULTS") {
            toast.error("Could not calculate route. Please verify locations.");
          }
        }
      }
    );
  };

  // const handlePlaceChange = async (type: "start" | "end") => {
  //   if (!isLoaded || !window.google) return;

  //   const latLngToString = (loc: google.maps.LatLng | undefined) => {
  //     if (!loc) return "";
  //     return `${loc.lat().toFixed(6)}, ${loc.lng().toFixed(6)}`;
  //   };

  //   let startPlace, endPlace;

  //   if (type === "start" && startAutocomplete) {
  //     startPlace = startAutocomplete.getPlace();
  //     if (startPlace?.formatted_address) {
  //       setStartAddress(startPlace.formatted_address);
  //       formik.setFieldValue("source_name", startPlace.formatted_address);
  //     }
  //     if (startPlace?.geometry?.location) {
  //       formik.setFieldValue("source_coordinates", latLngToString(startPlace.geometry.location));
  //     }
  //   }

  //   if (type === "end" && endAutocomplete) {
  //     endPlace = endAutocomplete.getPlace();
  //     if (endPlace?.formatted_address) {
  //       setEndAddress(endPlace.formatted_address);
  //       formik.setFieldValue("destination_name", endPlace.formatted_address);
  //     }
  //     if (endPlace?.geometry?.location) {
  //       formik.setFieldValue("destination_coordinates", latLngToString(endPlace.geometry.location));
  //     }
  //   }

  //   // BIG CHANGE: Use formik.values instead of autocomplete.getPlace()
  //   if (
  //     formik.values.source_coordinates &&
  //     formik.values.destination_coordinates &&
  //     window.google
  //   ) {
  //     const directionsService = new window.google.maps.DirectionsService();
  //     directionsService.route(
  //       {
  //         origin: new window.google.maps.LatLng(
  //           parseFloat(formik.values.source_coordinates.split(",")[0].trim()),
  //           parseFloat(formik.values.source_coordinates.split(",")[1].trim())
  //         ),
  //         destination: new window.google.maps.LatLng(
  //           parseFloat(formik.values.destination_coordinates.split(",")[0].trim()),
  //           parseFloat(formik.values.destination_coordinates.split(",")[1].trim())
  //         ),
  //         travelMode: window.google.maps.TravelMode.DRIVING,
  //       },
  //       (result, status) => {
  //         if (status === window.google.maps.DirectionsStatus.OK && result) {
  //           setDirectionsResponse(result);
  //           const leg = result.routes[0].legs[0];
  //           const distanceText = leg.distance?.text || "";
  //           const distanceValue = parseFloat(distanceText.replace(/[^\d.]/g, ""));
  //           const isKm = distanceText.includes("km");
  //           const miles = isKm ? distanceValue * 0.621371 : distanceValue;
  //           const finalMiles = formik.values.route_trip ? miles * 2 : miles;
  //           setDistance(`${finalMiles.toFixed(2)} miles`);
  //           formik.setFieldValue("distance", finalMiles.toFixed(2));
  //         }
  //       }
  //     );
  //   }
  // };

  const handlePlaceChange = (type: "start" | "end") => {
    if (!isLoaded || !window.google) return;

    const autocomplete = type === "start" ? startAutocomplete : endAutocomplete;
    const place = autocomplete?.getPlace();

    if (!place?.geometry?.location) {
      // Invalid place selected
      return;
    }

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const coords = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    const address = place.formatted_address || place.name || "";

    if (type === "start") {
      setStartAddress(address);
      formik.setFieldValue("source_name", address);
      formik.setFieldValue("source_coordinates", coords);
    } else {
      setEndAddress(address);
      formik.setFieldValue("destination_name", address);
      formik.setFieldValue("destination_coordinates", coords);
    }

    // Trigger route calculation after a tiny delay to ensure state updates
    // setTimeout(calculateRoute, 100);
  };

  useEffect(() => {
    // Only calculate if both coordinates exist and Google is loaded
    if (
      isLoaded &&
      formik.values.source_coordinates &&
      formik.values.destination_coordinates &&
      window.google
    ) {
      // Small delay to ensure state is settled (especially after Autocomplete)
      const timer = setTimeout(() => {
        calculateRoute();
      }, 300);

      return () => clearTimeout(timer);
    } else {
      // Clear route if coordinates are invalid
      setDirectionsResponse(null);
      setDistance("");
      formik.setFieldValue("distance", "");
    }
  }, [
    formik.values.source_coordinates,
    formik.values.destination_coordinates,
    formik.values.route_trip,
    isLoaded,
  ]);

  // Handle round trip toggle
  const handleRoundTripChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    formik.setFieldValue("route_trip", checked);

    if (distance) {
      const numericMiles = parseFloat(distance);
      const newMiles = checked ? numericMiles * 2 : numericMiles / 2;
      setDistance(`${newMiles.toFixed(2)} miles`);
      formik.setFieldValue("distance", newMiles);
    }
  };

  const handleFileSelect = (newFiles: File[]) => {
    // Filter valid files
    const validFiles = newFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`"${file.name}" exceeds 10 MB limit`);
        return false;
      }
      // if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      //   toast.error(`"${file.name}" is not an image or PDF`);
      //   return false;
      // }
      return true;
    });

    if (validFiles.length === 0) return;

    const currentTotalSize = receiptFiles.reduce((acc, f) => acc + f.size, 0);
    const newTotalSize = validFiles.reduce((acc, f) => acc + f.size, 0);
    const futureTotalSize = currentTotalSize + newTotalSize;
    const futureFileCount = receiptFiles.length + receiptImages.length + validFiles.length;

    if (futureFileCount > MAX_FILES) {
      toast.error(`You can only upload up to ${MAX_FILES} files total`);
      return;
    }

    if (futureTotalSize > MAX_TOTAL_SIZE) {
      toast.error("Total upload size cannot exceed 50 MB");
      return;
    }

    setReceiptFiles((prev) => [...prev, ...validFiles]);
    setUploadError("");
    toast.success(`${validFiles.length} file(s) added`);
  };


  return (
    <div className="layout ">
      <Sidebar isOpen={isSidebarOpen} />

      <main className="content">
        <div className="profileSettings add-mileage">
          <div className=" rounded add-mileage-inner">
            <div className="add-properties-heading d-flex align-items-center gap-3 mb-4">
              <ClockYellowIcon />
              <h2 className="mb-0">Log Mileage</h2>
            </div>

            <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
              {/* Property Dropdown */}
              <div className="row g-3 mb-3">
                <div
                  className="col-12 col-sm-12 col-md-12 col-lg-12"
                  ref={dropdownRef}
                >
                  <label className="form-label">Property</label>
                  <div className="dropdown ">
                    <button
                      type="button"
                      className="form-select d-flex justify-content-between align-items-center bg-light form-control commonInput "
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      disabled={isEdit}
                    >
                      {allProperties?.find(
                        (p) => p?.uuid === formik?.values?.property
                      )?.name || "Select Property"}
                      <i
                        className={`bi bi-chevron-${isDropdownOpen ? "up" : "down"}`}
                      />
                    </button>

                    {isDropdownOpen && (
                      <div
                        className="dropdown-menu proprty-dropdown show w-100 p-3 shadow-sm mt-1 bg-light"
                        style={{ maxHeight: "initial" }}
                      >
                        <div className="input-group mb-3 commonInput bg-light bg-white">
                          <span className="input-group-text bg-white border-end-0 ">
                            <SearchIcon />
                          </span>
                          <input
                            type="text"
                            placeholder="Search properties by name or address"
                            className="name form-control border-start-0"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e?.target?.value)}
                          />
                        </div>

                        {filteredProperties?.length > 0 ? (
                          filteredProperties?.map((property) => (
                            <div
                              key={property?.uuid}
                              className="property-drop form-check d-flex align-items-left mb-3 justify-content-between form-control commonInput  bg-light"
                              onClick={() => {
                                formik.setFieldValue("property", property.uuid);
                                setIsDropdownOpen(false);
                              }}
                              style={{ cursor: "pointer" }}
                            >
                              <label className="form-check-label ms-2">
                                <strong>{property?.name}</strong>
                                <small className="text-muted">
                                  {property?.address}, {property?.city},{" "}
                                  {property?.state} {property?.zipcode}
                                </small>
                              </label>
                              <input
                                type="radio"
                                className="form-check-input mt-1"
                                name="propertyRadio"
                                checked={
                                  formik?.values?.property === property?.uuid
                                }
                                readOnly
                              />
                            </div>
                          ))
                        ) : (
                          <div className="text-muted text-center">
                            No results found.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {formik.touched.property && formik.errors.property && (
                    <div className="text-danger">{formik.errors.property}</div>
                  )}
                </div>
              </div>

              {/* Location Feild */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Starting Location</label>
                    <div className="Mileage-modal position-relative">
                      <LocationIcon />

                      {isLoaded ? (
                        <Autocomplete
                          onLoad={(auto) => setStartAutocomplete(auto)}
                          onPlaceChanged={() => handlePlaceChange("start")}
                        >
                          <input
                            type="text"
                            name="source_name"
                            className="commonInput form-control bg-light"
                            placeholder="Enter your Starting Location"
                            value={startAddress}
                            onChange={(e) => setStartAddress(e.target.value)}
                            disabled={isEdit}
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          className="commonInput form-control bg-light"
                          placeholder="Loading..."
                          disabled
                        />
                      )}
                    </div>
                    {formik.touched.source_name &&
                      formik.errors.source_name && (
                        <div className="text-danger">
                          {formik.errors.source_name}
                        </div>
                      )}
                  </div>
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <div className="mb-3">
                    <label className="form-label">Destination Location</label>
                    <div className="Mileage-modal position-relative">
                      <LocationIcon />

                      {isLoaded ? (
                        <Autocomplete
                          onLoad={(auto) => setEndAutocomplete(auto)}
                          onPlaceChanged={() => handlePlaceChange("end")}
                        >
                          <input
                            type="text"
                            name="destination_name"
                            className="commonInput form-control bg-light"
                            placeholder="Enter your Destination Location"
                            value={endAddress}
                            onChange={(e) => setEndAddress(e.target.value)}
                            disabled={isEdit}
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          className="commonInput form-control bg-light"
                          placeholder="Loading..."
                          disabled
                        />
                      )}
                      {formik.touched.destination_name &&
                        formik.errors.destination_name && (
                          <div className="text-danger">
                            {formik.errors.destination_name}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="row g-3 mb-3">
                <div className="col-12">
                  <label className="form-label">Map</label>
                  <div style={{ width: "100%", height: "400px" }}>
                    {mapLoading ? (
                      <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading map...</span>
                        </div>
                      </div>
                    ) : (
                      // <GoogleMap
                      //   center={directionsResponse ? undefined : defaultCenter}
                      //   zoom={directionsResponse ? 12 : 4}
                      //   onLoad={(map) => setMap(map)}
                      //   mapContainerStyle={{ width: "100%", height: "100%" }}
                      // >
                      //   {directionsResponse && <DirectionsRenderer directions={directionsResponse} />}
                      // </GoogleMap>
                      <GoogleMap
                        center={defaultCenter}
                        zoom={4}
                        onLoad={(map) => setMap(map)}
                        mapContainerStyle={{ width: "100%", height: "100%" }}
                      >
                        {directionsResponse && (
                          <DirectionsRenderer
                            directions={directionsResponse}
                            options={{
                              suppressMarkers: false,
                              polylineOptions: { strokeColor: "#1976d2", strokeWeight: 5 },
                            }}
                          />
                        )}
                      </GoogleMap>
                    )}
                  </div>
                  {distance && (
                    <div className="mt-2 fw-bold text-primary">
                      Distance: {distance}
                    </div>
                  )}
                </div>
              </div>

              {/* Round Trip Checkbox */}
              <div className="row g-3 mb-3 mt-2">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                  <div className="form-check d-flex align-items-center gap-3">
                    <input
                      className="form-check-input me-0"
                      type="checkbox"
                      id="termsCheck"
                      checked={formik?.values?.route_trip}
                      onChange={handleRoundTripChange}
                      disabled={isEdit}
                    />
                    <label className="form-check-label" htmlFor="termsCheck">
                      Round Trip
                    </label>
                  </div>
                </div>
              </div>

              {/* Miles */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                  <div className="mb-3">
                    <label className="form-label">Miles</label>
                    <div className="input-group Mileage-modal position-relative">
                      <TimerGreyIcon />
                      <input
                        type="text"
                        name="distance"
                        className="form-control commonInput bg-light"
                        placeholder="Calculated miles"
                        value={distance || ""}
                        readOnly
                        disabled={isEdit}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Trip Purpose */}
              <div className="mb-4">
                <label htmlFor="taskDescription" className="form-label ">
                  Trip Purpose
                </label>
                <textarea
                  id="taskDescription"
                  name="purpose"
                  className="form-control commonInput bg-light"
                  rows={4}
                  maxLength={500}
                  placeholder="Detailed description of business purpose for this trip..."
                  value={formik.values.purpose}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isEdit}
                />
                <div className="text-end text-muted small mt-1">
                  {formik.values.purpose.length}/500 Characters
                </div>
                {formik.touched.purpose && formik.errors.purpose && (
                  <div className="text-danger">{formik.errors.purpose}</div>
                )}
              </div>

              {/* Activity Category */}
              <div className="user-type-box mb-4 p-3 rounded bg-light form-control commonInput activity-category">
                <label className="form-label">Activity Category</label>
                <div className="btn-radio d-flex flex-wrap gap-3">
                  {mainCategories.map((category) => (
                    <div key={category.value} className="me-3">
                      <input
                        type="radio"
                        id={category.value}
                        name="main_category"
                        value={category.value}
                        checked={formik.values.main_category === category.value}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={isEdit}
                      />
                      <label
                        htmlFor={category.value}
                        className="radio-label ms-2"
                      >
                        {category.label}
                      </label>
                    </div>
                  ))}
                </div>
                {formik.touched.main_category &&
                  formik.errors.main_category && (
                    <div className="text-danger">
                      {formik.errors.main_category}
                    </div>
                  )}
              </div>

              {/* Sub Activity & Activities */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Sub Activity</label>
                  <select
                    name="sub_activity"
                    className="form-select form-control commonInput bg-light"
                    value={formik?.values?.sub_activity}
                    onChange={formik.handleChange}
                    disabled={isEdit}
                  >
                    <option className="first" value="" disabled selected hidden>
                      Choose a Sub Activity
                    </option>
                    {subActivities.map((sub) => (
                      <option key={sub?.value} value={sub?.value}>
                        {sub?.label}
                      </option>
                    ))}
                  </select>
                  {formik.touched?.sub_activity &&
                    formik.errors?.sub_activity && (
                      <div className="text-danger">
                        {formik.errors?.sub_activity}
                      </div>
                    )}
                </div>

                <div className="col-12 col-sm-12 col-md-12 col-lg-6">
                  <label className="form-label">Activity</label>
                  <select
                    name="activity"
                    className="form-select form-control commonInput bg-light"
                    value={formik.values.activity}
                    onChange={formik.handleChange}
                    disabled={isEdit}
                  >
                    <option className="first" value="" disabled selected hidden>
                      Choose Activity
                    </option>
                    {activities.map((act) => (
                      <option key={act.value} value={act.value}>
                        {act.label}
                      </option>
                    ))}
                  </select>
                  {formik.touched.activity && formik.errors.activity && (
                    <div className="text-danger">{formik.errors.activity}</div>
                  )}
                </div>
              </div>

              {/* Add Description Section (conditionally rendered) */}
              {showActivityDescription && (
                <div className="mb-4">
                  <label htmlFor="activity_description" className="form-label">
                    Add Description
                  </label>
                  <textarea
                    id="activity_description"
                    name="activity_description"
                    className="form-control commonInput bg-light"
                    rows={4}
                    maxLength={500}
                    placeholder="Enter Additional Description"
                    value={formik.values.activity_description || ""}
                    onChange={formik.handleChange}
                    disabled={isEdit}
                  />
                  <div className="text-end text-muted small mt-1">
                    {formik.values.activity_description.length}/500 Characters
                  </div>
                  {formik.touched.activity_description &&
                    formik.errors.activity_description && (
                      <div className="text-danger">
                        {formik.errors.activity_description}
                      </div>
                    )}
                </div>
              )}

              {/* Date */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    name="date"
                    className="form-control commonInput bg-light"
                    value={formik.values.date}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEdit}
                  />
                  {formik.touched.date && formik.errors.date && (
                    <div className="text-danger">{formik.errors.date}</div>
                  )}
                </div>
              </div>

              {/* Recurring Trips Section */}
              <div className="row g-3 mb-3">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 ">
                  <div className="recurring-trips bg-light rounded  mb-4 form-control commonInput">
                    {/* Recurring Trips Checkbox */}
                    <div className="form-check  d-flex  gap-3  align-content-center">
                      <input
                        className="form-check-input me-0"
                        type="checkbox"
                        id="recurringTrips"
                        checked={isRecurring}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setIsRecurring(checked);
                          formik.setFieldValue("is_recurring", checked);
                          if (!checked) {
                            setSelectedDays([]);
                            formik.setFieldValue("frequencies", "");
                          } else if (checked && selectedDays.length === 0) {
                            // Default to all days if no days are selected yet
                            setSelectedDays([...recurringDays]);
                            formik.setFieldValue("frequencies", "everyday");
                          }
                        }}
                        onBlur={formik.handleBlur}
                      />
                      <label
                        className="form-check-label fw-bold"
                        htmlFor="recurringTrips"
                      >
                        Recurring Trips
                      </label>
                    </div>

                    {/* Show only if checked */}
                    {isRecurring && (
                      <>
                        {/* Everyday Checkbox */}
                        <div className="form-check mb-3 gap-3 mt-3 d-flex align-items-center">
                          <input
                            className="form-check-input me-0"
                            type="checkbox"
                            id="everyday"
                            checked={
                              selectedDays?.length === recurringDays?.length
                            }
                            onChange={(e) =>
                              handleEverydayToggle(e.target.checked)
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="everyday"
                          >
                            Everyday
                          </label>
                        </div>

                        {/* Days of the Week */}
                        <div className="d-flex gap-2 flex-wrap week-days">
                          {recurringDays.map((day, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => handleDayToggle(day)}
                              className={`btn rounded-circle ${selectedDays.includes(day.toLowerCase())
                                ? "btn-primary text-white"
                                : "btn-outline-primary"
                                }`}
                              style={{
                                width: "40px",
                                height: "40px",
                                padding: 0,
                              }}
                              disabled={
                                selectedDays?.length === recurringDays?.length
                              }
                            >
                              {day[0].toUpperCase()}
                            </button>
                          ))}
                        </div>
                        {formik.touched.frequencies &&
                          formik.errors.frequencies && (
                            <div className="text-danger">
                              {formik.errors.frequencies}
                            </div>
                          )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Upload Supporting Documents */}
              <div className="border rounded p-3 mb-4 button bg-light Enhanced-Information-Section">
                <div className="mb-3 upload-section">
                  <label className="form-label d-flex justify-content-left align-items-center gap-2">
                    <UploadIcon />
                    Upload Supporting Documents
                  </label>

                  <div
                    className="upload-area rounded text-center p-5 bg-white"
                    style={{
                      borderStyle: "dashed",
                      borderWidth: "2px",
                      borderColor: "#dee2e6",
                      cursor: "pointer",
                      pointerEvents: isEdit ? "none" : "auto",
                      opacity: isEdit ? 0.6 : 1
                    }}
                    onClick={isEdit ? (e) => e.preventDefault() : () => document.getElementById("fileUploadInput")?.click()}
                    onDragOver={isEdit ? (e) => e.preventDefault() : (e) => e.preventDefault()}
                    onDrop={isEdit ? (e) => e.preventDefault() : (e) => {
                      e.preventDefault();
                      const newFiles = Array.from(e.dataTransfer.files);
                      handleFileSelect(newFiles);
                    }}
                  >
                    <FileUploadWhiteIcon />
                    <p className="mb-0 text-muted">
                      Click or drag file to this area to upload
                    </p>

                    <input
                      type="file"
                      id="fileUploadInput"
                      hidden
                      multiple
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          const newFiles = Array.from(e.target.files);
                          handleFileSelect(newFiles);
                          e.target.value = ""; // Reset input
                        }
                      }}
                      disabled={isEdit}
                    />
                  </div>

                  {/* Inline error */}
                  {uploadError && (
                    <div className="text-danger mt-2">{uploadError}</div>
                  )}

                  {/* Show uploaded files and API receipt images */}
                  {(receiptFiles?.length > 0 || receiptImages?.length > 0) && (
                    <div className="mt-3 uploaded-files-section">

                      <div className="mb-3 p-2 bg-light rounded border">
                        <small className="text-muted fw-semibold">
                          Uploaded: {receiptFiles.length + receiptImages.length} / {MAX_FILES} files |
                          Size: {(receiptFiles.reduce((acc, f) => acc + f.size, 0) / (1024 * 1024)).toFixed(2)} MB / 50 MB
                        </small>
                        {receiptFiles.reduce((acc, f) => acc + f.size, 0) > MAX_TOTAL_SIZE * 0.9 && (
                          <span className="text-warning ms-2">Warning: Near limit!</span>
                        )}
                      </div>

                      <div className="mt-2 text-start">
                        {receiptImages.map((image, idx) => (
                          <div
                            key={image?.uuid}
                            className="d-flex justify-content-between align-items-center mb-1"
                          >
                            <a
                              href={image?.image}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {image?.image.split("/").pop()}
                            </a>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleOpenDeleteModal(image.uuid)}
                              disabled={isEdit}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        ))}

                        {receiptFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="d-flex justify-content-between align-items-center mb-1"
                          >
                            <span>{file?.name}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                setReceiptFiles((prev) => {
                                  const updated = prev.filter(
                                    (_, i) => i !== idx
                                  );
                                  if (
                                    updated.length + receiptImages.length <=
                                    MAX_FILES
                                  ) {
                                    setUploadError("");
                                  }
                                  return updated;
                                });
                              }}
                              disabled={isEdit}
                            >
                              <i className="fas fa-times cross-btn"></i>
                            </button>
                          </div>
                        ))}
                      </div>

                    </div>
                  )}
                </div>
              </div>

              {/* Travel Checkbox */}
              <div className="activity-involve-travel-area bg-light mb-4 form-control commonInput flex-wrap">
                <div className="activity-involve-travel-inner form-check form-switch-1 bg-warning-subtle px-4 py-2 rounded ">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="travelCheckbox"
                    checked={isTravelInvolved}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsTravelInvolved(checked);
                      if (!checked) formik.setFieldValue("logHourData", null);
                    }}
                    onBlur={formik.handleBlur}
                    disabled={isEdit}
                  />
                  <TravelActivityIcon />
                  <label
                    className="form-check-label ms-2"
                    htmlFor="travelCheckbox"
                  >
                    Want to add log time for this trip ?
                  </label>
                  {isTravelInvolved && (
                    <div className=" Mileage-Entry">
                      <button
                        type="button"
                        className="btn btn-warning btn-sm"
                        onClick={() => setShowLogHourModal(true)}
                        disabled={isEdit}
                      >
                        Quick Link to Log Hour Entry
                        <ArrowUpWhiteIcon />
                      </button>
                    </div>
                  )}
                </div>
                {formik.touched.logHourData && formik.errors.logHourData && (
                  <div className="text-danger">{formik.errors.logHourData}</div>
                )}
              </div>

              {/* Submit Button */}
              <div className="text-end">
                <button
                  type="submit"
                  className="log-time-button btn primaryButton  btn-primary d-inline-flex justify-content-center align-items-center btn-block"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  {isEdit ? "Update" : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <LogHourEntryModal
        show={showLogHourModal}
        onHide={() => setShowLogHourModal(false)}
        data={logHourData}
        onSave={handleLogHourSave}
      />

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type="deleteMileageReceipt"
        uuid={selectedImageUuid}
        data={{ uuid: id }}
        onSuccess={handleDeleteSuccess}
      />
    </div>
  );
};

export default AddMileage;
